import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "react-hot-toast";
import { activityService } from "@/services/activityService";
import type { ActivityCreate } from "@/types";
import { CreateActivityModal } from "@/pages/activities/components/CreateActivityModal";
import { PageFrame } from "@/components/common";
import { toastApiError } from "@/lib/errorMessages";
import { useGoBack } from "@/hooks/useGoBack";

export default function AdminCreateActivityPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("activities");

  const ownerFromPage = searchParams.get("owner_user_id") ?? undefined;
  const isHostContext = searchParams.get("context") === "host";

  const navigateAfterSuccess = () => {
    if (!ownerFromPage) {
      navigate("/activities");
      return;
    }
    if (isHostContext) {
      navigate(`/hosts/${ownerFromPage}`);
    } else {
      navigate(`/sellers/${ownerFromPage}`);
    }
  };

  const backFallback = !ownerFromPage
    ? "/activities"
    : isHostContext
      ? `/hosts/${ownerFromPage}`
      : `/sellers/${ownerFromPage}`;
  const goBack = useGoBack(backFallback);

  const createMutation = useMutation({
    mutationFn: (data: ActivityCreate) => activityService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      if (ownerFromPage) {
        if (isHostContext) {
          queryClient.invalidateQueries({ queryKey: ["admin", "host", ownerFromPage] });
          queryClient.invalidateQueries({
            queryKey: ["admin", "host", ownerFromPage, "activities"],
          });
          queryClient.invalidateQueries({ queryKey: ["host", ownerFromPage] });
          queryClient.invalidateQueries({
            queryKey: ["host-houses", ownerFromPage],
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["admin", "seller", ownerFromPage] });
          queryClient.invalidateQueries({
            queryKey: ["admin", "seller", ownerFromPage, "activities"],
          });
        }
      }
      toast.success(t("list.createdToast"));
      navigateAfterSuccess();
    },
    onError: (error) => {
      toastApiError(error, "createActivity");
    },
  });

  const backLabel = !ownerFromPage
    ? t("adminCreate.back")
    : isHostContext
      ? t("adminCreate.backToHost")
      : t("adminCreate.backToPartner");

  return (
    <PageFrame
      backLink={{
        variant: "ghost",
        fallback: backFallback,
        children: backLabel,
      }}
    >
      <CreateActivityModal
        isOpen
        fixedOwnerUserId={ownerFromPage}
        onClose={goBack}
        onSubmit={(data) => {
          if (createMutation.isPending) return;
          createMutation.mutate(data);
        }}
        isLoading={createMutation.isPending}
      />
    </PageFrame>
  );
}
